import { useState } from "react";
import { Download, Package, ArrowLeft, CheckCircle2, Loader2, ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { downloadZip } from "client-zip";
import { supabase } from "@/integrations/supabase/client";
import { UploadedFile, SessionMeta, buildFileName, formatTime } from "@/lib/tape";
import { clientTrimMp4 } from "@/lib/trim-mp4";
import { devLog, devWarn, logError } from "@/lib/logger";

interface Props {
  files: UploadedFile[];
  meta: SessionMeta;
  onBack: () => void;
  onReset: () => void;
}

interface ProcessedResult {
  blob: Blob;
  fellBack: boolean;
}

// Containers we can pass through untouched when no trim is applied.
// iPhone tapes (.mov / video/quicktime) and standard MP4s both qualify.
function isPassthroughContainer(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return (
    type.includes("mp4") ||
    type.includes("quicktime") ||
    name.endsWith(".mp4") ||
    name.endsWith(".m4v") ||
    name.endsWith(".mov") ||
    name.endsWith(".qt")
  );
}

async function processForExport(f: UploadedFile, onProgress?: (msg: string) => void): Promise<ProcessedResult> {
  const dur = f.durationSec || 0;
  const trimmed = Math.abs(f.trimStart) > 0.05 || (dur > 0 && Math.abs(f.trimEnd - dur) > 0.05);
  devLog("[export] processing", {
    name: f.name,
    willTrim: trimmed,
  });

  // No trim + safe container → skip ffmpeg entirely. Huge speed win for
  // rename-only workflows on both .mp4 and iPhone .mov files.
  if (!trimmed && isPassthroughContainer(f.file)) {
    return { blob: f.file, fellBack: false };
  }

  try {
    const result = await clientTrimMp4(f.file, f.trimStart, f.trimEnd, f.durationSec, onProgress);
    if (!result.blob || result.blob.size === 0) {
      throw new Error("processing produced empty blob");
    }
    return {
      blob: result.blob,
      fellBack: !result.trimmed,
    };
  } catch (err) {
    devWarn("[export] falling back to original file", err);
    // Last-resort fallback: return the original file as-is so export never fails.
    return { blob: f.file, fellBack: true };
  }
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportStage({ files, meta, onBack, onReset }: Props) {
  const [zipping, setZipping] = useState(false);
  const [progressMsg, setProgressMsg] = useState<string>("Trimming & packaging…");
  const [savedSession, setSavedSession] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [trackerShown, setTrackerShown] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  function maybePromptTracker() {
    if (trackerShown) return;
    setTrackerShown(true);
    // Slight delay so it doesn't fight the download dialog
    setTimeout(() => setTrackerOpen(true), 300);
  }

  function goToAuditions() {
    const payload = {
      project_name: meta.projectName || "",
      role_name: meta.roleName || "",
      audition_date: new Date().toISOString().slice(0, 10),
      audition_format: "Self Tape",
    };
    const prefill = btoa(JSON.stringify(payload));
    setTrackerOpen(false);
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    navigate({ to: "/auditions", search: { prefill } });
  }

  async function persistSession() {
    if (savedSession) return;
    if (!user) return;
    try {
      await supabase.from("sessions").insert({
        user_id: user.id,
        first_name: meta.firstName || null,
        last_name: meta.lastName || null,
        project_name: meta.projectName || null,
        role_name: meta.roleName || null,
      });
      setSavedSession(true);
    } catch (e) {
      logError("session-save", e);
    }
  }

  async function downloadOne(f: UploadedFile) {
    setProgressMsg("Trimming…");
    setZipping(true);
    try {
      const { blob, fellBack } = await processForExport(f, (m) => setProgressMsg(m));
      triggerDownload(blob, buildFileName(meta, f));
      if (fellBack) {
        toast.message("Trim unavailable right now — downloading original file instead.");
      }
      persistSession();
      maybePromptTracker();
    } catch (e) {
      logError("export-one", e);
      toast.error("Export failed. Please try again.");
    } finally {
      setZipping(false);
      setProgressMsg("Trimming & packaging…");
    }
  }

  async function downloadAll() {
    if (files.length === 0) {
      toast.error("No files to export");
      return;
    }
    setProgressMsg("Preparing files…");
    setZipping(true);
    try {
      let fallbackCount = 0;
      let index = 0;

      // Async-iterable source: each clip is processed lazily, then yielded
      // to client-zip. Once client-zip consumes a chunk the previous Blob
      // becomes unreferenced and can be GC'd — peak memory stays low.
      async function* sources() {
        for (const f of files) {
          index++;
          setProgressMsg(`Processing ${index}/${files.length}…`);
          try {
            const { blob, fellBack } = await processForExport(f, (m) => setProgressMsg(m));
            if (fellBack) fallbackCount++;
            const safeBlob = blob && blob.size > 0 ? blob : f.file;
            yield {
              name: buildFileName(meta, f),
              input: safeBlob,
              lastModified: new Date(),
            };
          } catch (err) {
            devWarn("[export] processing failed, using original", err);
            fallbackCount++;
            yield {
              name: buildFileName(meta, f),
              input: f.file,
              lastModified: new Date(),
            };
          }
        }
        setProgressMsg("Building ZIP…");
      }

      // client-zip: STORE-only (videos don't compress), Web Streams native,
      // far less memory churn than JSZip's internal buffers.
      const response = downloadZip(sources());
      const zipBlob = await response.blob();
      const zipName =
        `${meta.lastName || "Tapes"}_${meta.projectName || "Project"}_${meta.roleName || "Role"}.zip`.replace(
          /[^a-zA-Z0-9_.\-]/g,
          "_",
        );
      triggerDownload(zipBlob, zipName);
      persistSession();
      maybePromptTracker();
      if (fallbackCount > 0) {
        toast.message("Trim unavailable right now — downloading original file instead.");
      } else {
        toast.success("ZIP ready");
      }
    } catch (e) {
      logError("export-zip", e);
      toast.error("Failed to build ZIP. Please try again.");
    } finally {
      setZipping(false);
      setProgressMsg("Trimming & packaging…");
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Files ready</h1>
        <p className="text-muted-foreground mt-2 text-base">
          Casting-ready filenames below. Download all at once or grab them individually.
        </p>
      </div>

      {/* Primary CTA */}
      <Card className="p-6 bg-card border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-full bg-success/15 text-success grid place-items-center">
            <CheckCircle2 className="size-5" />
          </div>
          <div>
            <p className="font-semibold">
              {files.length} file{files.length === 1 ? "" : "s"} ready
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Renamed and packaged for casting</p>
          </div>
        </div>
        <Button
          size="lg"
          onClick={downloadAll}
          disabled={zipping}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
        >
          {zipping ? (
            <>
              <Loader2 className="size-4 mr-2 animate-spin" />
              {progressMsg}
            </>
          ) : (
            <>
              <Package className="size-4 mr-2" />
              Download all files
            </>
          )}
        </Button>
      </Card>

      {/* File list */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold">Individual files</h2>
        <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-4 p-4">
              <CheckCircle2 className="size-4 text-success shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm truncate">{buildFileName(meta, f)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Trim {formatTime(f.trimStart)} → {formatTime(f.trimEnd)} · {formatTime(f.trimEnd - f.trimStart)}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => downloadOne(f)} disabled={zipping}>
                <Download className="size-3.5 mr-1.5" />
                Download
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between gap-3 pt-2">
        <Button variant="outline" size="lg" onClick={onBack}>
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
        <Button variant="ghost" size="lg" onClick={onReset}>
          Start over
        </Button>
      </div>

      <Dialog open={trackerOpen} onOpenChange={setTrackerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListPlus className="size-5 text-primary" />
              Add this audition to your tracker?
            </DialogTitle>
            <DialogDescription>
              Log it now so it shows up in your audition history and insights. Your download is unaffected.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Project</span>
              <span className="font-medium">{meta.projectName || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium">{meta.roleName || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Date</span>
              <span className="font-medium">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Format</span>
              <span className="font-medium">Self Tape</span>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setTrackerOpen(false)}>
              Skip
            </Button>
            <Button onClick={goToAuditions}>
              <ListPlus className="size-4 mr-1.5" />
              Add to Auditions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
